import React, { useEffect, useState } from "react";
import {
    Stack,
    Typography,
    Select,
    MenuItem,
    Checkbox,
    useTheme,
    Box,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import LocalOfferRounded from "@mui/icons-material/LocalOfferRounded";
import GroupsRounded from "@mui/icons-material/GroupsRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import dayjs from "dayjs";
import Header from "../Header";
import TabHeader from "../TabHeader";
import ContentAreaWrapper from "../ContentAreaWrapper";
import AIAdmin from "../../api/AIAdmin";
import BottomButton from "../BottomButton";
import User from "../../api/User";
import StatusScreen from "../StatusScreen";
import { FONTS, CARD_STYLES, FORM_LABEL_SX } from "../../constants/styles";

const FillPromoConfig = () => {
    const WAITLIST_KEY = "__waitlist__";

    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const [params] = useSearchParams();
    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const navigate = useNavigate();

    const uuid = cookies.uuid;
    const brand_id = cookies.brand_id;
    const location_id = cookies.location_id;
    const date = params.get("date");

    const isPastDate =
        date && dayjs(date).isBefore(dayjs().startOf("day"));

    const [discount, setDiscount] = useState(10);

    const [targetOptions, setTargetOptions] = useState<string[]>([]);
    const [selectedTargets, setSelectedTargets] = useState<string[]>([]);

    const [loading, setLoading] = useState(false);

    const iconBoxSx = {
        width: 32,
        height: 32,
        borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    useEffect(() => {
        if (!uuid || !brand_id || !location_id || !date) {
            navigate("/");
        }
    }, [uuid, brand_id, location_id, date, navigate]);

    useEffect(() => {
        if (!uuid || !brand_id) return;

        User.getStatuses(uuid, brand_id).then((statuses) => {
            const fullList = [WAITLIST_KEY, ...statuses];
            setTargetOptions(fullList);
            setSelectedTargets(fullList);
        });
    }, [uuid, brand_id]);

    const handleSubmit = async () => {
        if (!uuid || !brand_id || !location_id || !date) return;

        setLoading(true);
        try {
            const response = await AIAdmin.fillMyDay(
                uuid,
                brand_id,
                location_id,
                {
                    date,
                    discount,
                    target: {
                        waitlist: selectedTargets.includes(WAITLIST_KEY),
                        statuses: selectedTargets.filter(
                            (s) => s !== WAITLIST_KEY
                        ),
                    },
                }
            );

            if (!response.ok) throw new Error(response.error);

            navigate("/calendar", {
                state: {
                    fillMyDaySuccess: true,
                },
            });
        } catch (err: any) {
            console.error("Error submitting Fill My Day:", err);
            navigate("/calendar", {
                state: {
                    error: err?.message ?? "Something went wrong.",
                },
            });
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/calendar");
        }
    };

    const toggleTarget = (option: string) => {
        setSelectedTargets((prev) =>
            prev.includes(option)
                ? prev.filter((s) => s !== option)
                : [...prev, option]
        );
    };

    if (isPastDate) {
        return (
            <StatusScreen
                icon={HistoryRounded}
                iconColor={primary}
                title="Time Travel Not Supported"
                subtitle="You can only fill upcoming days. This date has already passed."
                description={`Selected: ${dayjs(date).format("MMMM D, YYYY")}`}
                buttonLabel="Back to Calendar"
                onButtonClick={() => navigate("/calendar")}
            />
        );
    }

    return (
        <>
            <Header />
            <TabHeader name="Fill My Day" onClick={handleBack} />
            <ContentAreaWrapper headerType="tab" noFooter stickyBottomButton>
                <Stack
                    width="100%"
                    alignItems="center"
                    spacing={3}
                    pt={1}
                >
                    {/* Section: Discount */}
                    <Stack spacing={1} width="100%">
                        <Typography
                            fontSize="11px"
                            fontWeight={600}
                            color="text.secondary"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                        >
                            Promotion
                        </Typography>
                        <Stack sx={CARD_STYLES} spacing={1.5}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <LocalOfferRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Discount for the day
                                </Typography>
                            </Stack>
                            <Select
                                value={discount}
                                onChange={(e) =>
                                    setDiscount(Number(e.target.value))
                                }
                                size="small"
                                sx={{
                                    width: "100px",
                                    borderRadius: "10px",
                                    bgcolor: alpha(primary, 0.04),
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: alpha(primary, 0.12),
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: alpha(primary, 0.25),
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: primary,
                                    },
                                }}
                            >
                                {[0, 5, 10, 15, 20, 25, 30, 40, 50].map(
                                    (val) => (
                                        <MenuItem
                                            key={val}
                                            value={val}
                                            sx={{
                                                fontFamily: FONTS.display,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {val}%
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </Stack>
                    </Stack>

                    {/* Section: Target audience */}
                    <Stack spacing={1} width="100%">
                        <Typography
                            fontSize="11px"
                            fontWeight={600}
                            color="text.secondary"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                        >
                            Target Audience
                        </Typography>
                        <Stack sx={CARD_STYLES} spacing={0.5}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                mb={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <GroupsRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Who should get the offer?
                                </Typography>
                            </Stack>

                            {targetOptions.map((option) => {
                                const isChecked =
                                    selectedTargets.includes(option);
                                const label =
                                    option === WAITLIST_KEY
                                        ? "Waitlist"
                                        : option;
                                return (
                                    <Stack
                                        key={option}
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        onClick={() => toggleTarget(option)}
                                        sx={{
                                            py: 0.75,
                                            px: 1,
                                            borderRadius: "10px",
                                            cursor: "pointer",
                                            bgcolor: isChecked
                                                ? alpha(primary, 0.04)
                                                : "transparent",
                                            transition:
                                                "background-color 0.15s ease",
                                            "&:hover": {
                                                bgcolor: alpha(primary, 0.06),
                                            },
                                        }}
                                    >
                                        <Checkbox
                                            checked={isChecked}
                                            size="small"
                                            sx={{
                                                p: 0,
                                                color: alpha(primary, 0.3),
                                                "&.Mui-checked": {
                                                    color: primary,
                                                },
                                            }}
                                        />
                                        <Typography
                                            fontSize="14px"
                                            fontWeight={isChecked ? 500 : 400}
                                        >
                                            {label}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Stack>
                </Stack>
            </ContentAreaWrapper>
            <BottomButton sticky disabled={loading} onClick={handleSubmit}>
                Launch Promotion
            </BottomButton>
        </>
    );
};

export default FillPromoConfig;
